<template>
  <div>
    <!-- Breadcrumb -->
    <nav v-if="breadcrumb" class="border-b border-gray-300 py-2 px-2 mb-4 md:px-0">
      <div class="flex justify-between items-center">
        <div>
          <div class="hidden md:flex text-sm text-gray-500">
            <router-link :to="{ name: 'home' }" class="hover:text-primary">Home</router-link>
            <span class="mx-2">/</span>
            <router-link :to="{ name: 'organization', params: { orgId: orgId } }" class="hover:text-primary">Organization</router-link>
            <span class="mx-2">/</span>
            <router-link :to="{ name: 'customers' }" class="hover:text-primary">Connections</router-link>
          </div> 
          <div class="text-2xl text-primary font-bold mt-2">Connections</div>
        </div>
      </div>
    </nav>

    <!-- Filters + Export -->
    <div class="pb-4">
      <div class="bg-white border rounded-lg shadow p-4">
        <div class="flex justify-between items-center mb-4">
          <div class="font-semibold text-gray-700 text-lg">{{ title }}</div>

          <!-- Export Button -->
          <button
            class="bg-primary text-white px-4 py-2 rounded-lg shadow hover:bg-green-700 transition"
            @click="downloadCsv"
          >
            ⬇ Export CSV
          </button>
          <download-csv :data="exportData" name="connections.csv" class="hidden" ref="exportCsv"></download-csv>
        </div>

        <!-- Filters -->
        <div class="flex flex-col md:flex-row md:justify-between gap-4 mb-4">
          <!-- Records per page -->
          <div>
            <label class="block text-gray-600 font-medium mb-1">Records per page</label>
            <select
              v-model="recordsPerPage"
              @change="loadStats(1)"
              class="block w-full bg-white border border-gray-300 px-3 py-2 rounded-lg shadow-sm focus:outline-none focus:border-primary"
            >
              <option>5</option>
              <option>10</option>
              <option>25</option>
              <option>50</option>
              <option>100</option>
              <option>250</option>
            </select>
          </div>

          <!-- Search -->
          <div class="flex-1">
            <label class="block text-gray-600 font-medium mb-1">Search</label>
            <input
              v-model="searchString"
              @input="loadStats(1)"
              class="block w-full border border-gray-300 rounded-lg py-2 px-3 shadow-sm focus:outline-none focus:border-primary"
              type="text"
              placeholder="🔍 Search connections..."
            />
          </div>
        </div>

        <!-- Table -->
        <div class="overflow-x-auto rounded-lg shadow">
          <table v-if="!loading" class="table-auto w-full border-collapse">
            <thead>
              <tr class="bg-gray-100 text-gray-700 text-sm uppercase tracking-wide">
                <th class="px-4 py-3 text-left">#</th>
                <th class="px-4 py-3 text-left">Connected At</th>
                <th class="px-4 py-3 text-left">Customer</th>
                <th class="px-4 py-3 text-left">Device</th>
                <th class="px-4 py-3 text-left">Network</th>
                <th class="px-4 py-3 text-left">Access Point</th>
                <th class="px-4 py-3 text-left">Auth Mode</th>
                <th class="px-4 py-3 text-left">Duration</th>
                <th class="px-4 py-3 text-left">Consumption</th>
                <th class="px-4 py-3 text-center">Status</th>
                <th class="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(connection, index) in organization.connections"
                :key="connection.id"
                class="border-t hover:bg-gray-50 transition-colors"
              >
                <td class="px-4 py-3 font-bold">{{ offset + index + 1 }}</td>
                <td class="px-4 py-3">{{ formatDate(connection.authorized_at, "DD-MM-YYYY HH:mm") }}</td>
                <td class="px-4 py-3">
                  <router-link
                    class="text-primary font-medium hover:underline"
                    :to="{ name:'customer_profile', params:{customerId: connection.customer.id} }"
                  >
                    👤 {{ connection.customer.identity || connection.device_mac }}
                  </router-link>
                </td>
                <td class="px-4 py-3">💻 {{ connection.device.name }}</td>
                <td class="px-4 py-3">
                  <router-link
                    class="text-primary hover:underline"
                    :to="{ name: 'venue_details', params: { venueId: connection.venue.id } }"
                  >
                     {{ connection.venue.name }}
                  </router-link>
                </td>
                <td class="px-4 py-3">
                  <router-link
                    v-if="connection.accessPoint.id"
                    class="text-primary hover:underline"
                    :to="{ name: 'access_point', params: { apId: connection.accessPoint.id } }"
                  >
                     {{ connection.accessPoint.name }}
                  </router-link>
                  <span v-else class="text-gray-500">{{ connection.access_point_mac }}</span>
                </td>

                <!-- Badges added here -->
                <td class="px-4 py-3">
                  <span class="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                    {{ connection.authorization_mode }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span class="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                    {{ connection.duration }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span class="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
                    {{ connection.usage }}
                  </span>
                </td>
                <td class="px-4 py-3 text-center">
                  <span
                    class="px-2 py-1 rounded text-xs font-medium inline-flex items-center gap-1"
                    :class="connection.isOnline ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
                  >
                    <feather :type="connection.isOnline ? 'wifi' :'wifi-off'" size="14" />
                    {{ connection.isOnline ? 'Online' : 'Offline' }}
                  </span>
                </td>

                <td class="px-4 py-3 text-center">
                  <button
                    v-if="$store.getters.can('edit_connections') && connection.isOnline"
                    @click="closeConnections(connection)"
                    class="border rounded text-xs border-red-500 text-red-500 px-2 py-1 hover:bg-red-500 hover:text-white transition"
                  >
                    Close
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Loading -->
          <div v-else class="py-6 text-center w-full text-gray-600">
            <feather animation="spin" type="loader" size="32" class="mr-2"></feather>
            <p>Loading...</p>
          </div>
        </div>

        <!-- Pagination -->
        <Pagination
          class="py-4"
          :total-count="organization.connectionsCount"
          :current-page="currentPage"
          :tag="'connections'"
          :offset="offset"
          :records-count="organization.connections ? organization.connections.length : 0"
          :click-handler="loadStats"
          :records-per-page="parseInt(recordsPerPage)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import GraphQl from "../../services/graphql.service";
import Pagination from "@/components/Pagination";
import moment from "moment";

export default {
  name: "Connections",
  components: {Pagination},
  data() {
    return {
      exportData: [],
      searchString: '',
      recordsPerPage: this.rowsPerPage,
      currentPage: 1,
      loading: true,
      organization: Object,
      orgId: this.$store.getters.orgId
    }
  },
  props: {
    title: {type: String, default: 'Authorized connections'},
    configs: Object,
    accessPoints: {type: Array, default: () => []},
    devices: {type: Array, default: () => []},
    customers: {type: Array, default: () => []},
    venues: {type: Array, default: () => []},
    online: {type: Boolean, default: null},
    rowsPerPage: {type: Number, default: 10},
    authorized: {type: Boolean, default: true},
    breadcrumb: {default: true, type: Boolean},
    startDate: Date,
    endDate: Date
  },
  mounted() {
    this.loadStats();
  },

  computed: {
    offset: function () {
      return (this.currentPage - 1) * this.recordsPerPage;
    }
  },
  watch: {
    searchString: function () {
      this.currentPage = 1;
    },
    startDate: function () {
      this.loadStats(this.currentPage);
    },
    endDate: function () {
      this.loadStats(this.currentPage);
    },
    venues: function () {
      this.loadStats(this.currentPage);
    },
  },
  methods: {
    downloadCsv() {
      let params = (this.venues.length > 0 ? 'venues:["' + this.venues.join('","') + '"],' : '') +
          (this.customers.length > 0 ? 'customers:["' + this.customers.join('","') + '"],' : '') +
          (this.devices.length > 0 ? 'devices:["' + this.devices.join('","') + '"],' : '') +
          (this.accessPoints.length > 0 ? 'accessPoints:["' + this.accessPoints.join('","') + '"],' : '') +
          (this.online != null ? 'online:' + this.online + ',' : '') +
          (this.authorized != null ? 'authorized:' + this.authorized + ',' : '') +
          (this.startDate != null ? 'startDate: "' + this.startDate + '", ' : '') +
          (this.endDate != null ? 'endDate: "' + this.endDate + '", ' : '') +
          'limit: ' + this.organization.connectionsCount + ', ' +
          'offset: 0, ' +
          'order: "created_at desc", ' +
          'search: "' + this.searchString + '"';

      GraphQl.sendRequest('{organization(id:"' + this.$store.getters.orgId + '") {' +
          'connections(' + params + '){id isOnline status access_point_mac ' +
          'customer{identity id}' +
          'duration usage ssid ' +
          'venue{name id} ' +
          'accessPoint{name id}' +
          'upload download authorized_at created_at authorization_mode end_time}}}')
          .then(response => {
            response.organization.connections.forEach(connection => {
              this.exportData.push({
                id: connection.id,
                name: connection.customer.identity,
                network: connection.venue.name,
                ap: connection.accessPoint.name,
                apMac: connection.access_point_mac,
                ssid: connection.ssid,
                status: connection.status,
                connectionMode: connection.authorization_mode,
                upload: connection.upload,
                download: connection.download,
                usage: connection.usage,
                connectedAt: connection.authorized_at,
                disconnectedAt: connection.end_time,
                duration: connection.duration
              })
            });
          }).finally(() => {
        this.$refs.exportCsv.$el.click();
      });
    },
    closeConnections(connection) {
      GraphQl.sendRequest('mutation{closeConnection(id:"' + connection.id + '"){code message}}')
          .then(response => {
            this.$root.$emit(response.closeConnection.code === 200 ? 'alert_success' : 'alert_error', [response.closeConnection.message]);
          })
          .finally(() => {
            this.loadStats(this.currentPage)
          });
    },
    formatDate(timestamp, format) {
      return moment.utc(timestamp).format(format);
    },
    loadStats(newPage) {
      this.currentPage = newPage || 1;
      this.loading = true
      let params = (this.venues.length > 0 ? 'venues:["' + this.venues.join('","') + '"],' : '') +
          (this.customers.length > 0 ? 'customers:["' + this.customers.join('","') + '"],' : '') +
          (this.devices.length > 0 ? 'devices:["' + this.devices.join('","') + '"],' : '') +
          (this.accessPoints.length > 0 ? 'accessPoints:["' + this.accessPoints.join('","') + '"],' : '') +
          (this.online != null ? 'online:' + this.online + ',' : '') +
          (this.authorized != null ? 'authorized:' + this.authorized + ',' : '') +
          (this.startDate != null ? 'startDate: "' + this.startDate + '", ' : '') +
          (this.endDate != null ? 'endDate: "' + this.endDate + '", ' : '') +
          'limit: ' + this.recordsPerPage + ', ' +
          'offset: ' + this.offset + ', ' +
          'order: "created_at desc", ' +
          'search: "' + this.searchString + '"';

      GraphQl.sendRequest('{organization(id:"' + this.$store.getters.orgId + '") {' +
          'connectionsCount(' + params + ') ' +
          'connections(' + params + '){id isOnline status access_point_mac ' +
          'customer{identity id}' +
          'device{name}' +
          'duration usage ssid ' +
          'venue{name id} ' +
          'accessPoint{name id}' +
          'upload download authorized_at created_at authorization_mode end_time}}}')
          .then(response => {
            this.organization = response.organization;
          }).finally(() => {
        this.loading = false;
      })
    }
  }
}
</script>

<style scoped>

</style>